import React from 'react';
import { RotateCcw } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  FilterToolbarSearch,
  VirtualizedMultiSelectCombobox,
  filterToolbarControlMdClassName,
  filterToolbarControlSmClassName,
  filterToolbarSearchGrowClassName,
} from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import type { RemittanceTableQuery } from '../../hooks/useRemittanceTableQuery';
import { useStore } from '../../store';
import type {
  RemittancePayorOption,
  RemittanceSource,
} from '../../services/api/types.gen';
import { SOURCE_FILTER_OPTIONS } from './remittanceLabels';

export interface RemittanceTableToolbarProps {
  query: RemittanceTableQuery;
  payors: RemittancePayorOption[];
  onReset: () => void;
  /** Rendered right-aligned; the manager passes its Upload 835 button. */
  uploadSlot?: React.ReactNode;
}

export function RemittanceTableToolbar({
  query,
  payors,
  onReset,
  uploadSlot,
}: RemittanceTableToolbarProps) {
  // Local input state keeps typing responsive; the committed search is debounced
  // (~250ms) so each keystroke does not trigger a server round-trip.
  const [searchValue, setSearchValue] = React.useState(query.search);
  const { hideTestPatients, setHideTestPatients } = useStore(
    useShallow(state => ({
      // Page-wide slice shared with the claims and NOA toolbars, so toggling it
      // here carries across surfaces and persists through the store's
      // `persist` middleware.
      hideTestPatients: state.hideTestPatients,
      setHideTestPatients: state.setHideTestPatients,
    }))
  );

  React.useEffect(() => {
    setSearchValue(query.search);
  }, [query.search]);

  React.useEffect(() => {
    if (searchValue === query.search) return;
    const handle = setTimeout(() => query.setSearch(searchValue), 250);
    return () => clearTimeout(handle);
  }, [searchValue, query.search, query.setSearch]);

  return (
    <div className='mb-3 grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-end'>
      <FilterToolbarSearch
        value={searchValue}
        onChange={setSearchValue}
        label='Search'
        placeholder='Search...'
        containerClassName={`${filterToolbarSearchGrowClassName} col-span-2`}
        className='w-full'
      />

      {/* No status control here: status is the tab bar above the table, the
          same way the claims tabs own claim status. */}
      <VirtualizedMultiSelectCombobox
        options={payors.map(p => ({ value: p.id, label: p.name }))}
        value={query.payorIds}
        onValueChange={query.setPayorIds}
        placeholder='All Insurances'
        searchPlaceholder='Search insurances…'
        buttonClassName={`h-8 ${filterToolbarControlMdClassName}`}
      />
      <VirtualizedMultiSelectCombobox
        options={SOURCE_FILTER_OPTIONS}
        value={query.sources}
        onValueChange={v => query.setSources(v as RemittanceSource[])}
        placeholder='All Sources'
        searchPlaceholder='Search sources…'
        buttonClassName={`h-8 ${filterToolbarControlSmClassName}`}
      />

      {/* Date pickers and comboboxes match Search at default h-8. */}
      <DatePicker
        datePickerSize='default'
        showClearButton
        placeholder='Deposit from'
        date={query.depositDateFrom}
        onDateChange={query.setDepositDateFrom}
        className={filterToolbarControlSmClassName}
      />
      <DatePicker
        datePickerSize='default'
        showClearButton
        placeholder='Deposit to'
        date={query.depositDateTo}
        onDateChange={query.setDepositDateTo}
        className={filterToolbarControlSmClassName}
      />

      <div className='flex h-8 items-center gap-2 self-end col-span-2 md:col-auto'>
        <Checkbox
          id='remittance-hide-test-patients'
          checked={hideTestPatients}
          onCheckedChange={(checked: boolean) => {
            setHideTestPatients(!!checked);
          }}
          checkboxSize='sm'
        />
        <label
          htmlFor='remittance-hide-test-patients'
          className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
        >
          Hide test patients
        </label>
      </div>

      <Button
        variant='outline'
        size='sm'
        onClick={() => {
          // Clear the local input so a pending debounced search cannot re-apply
          // after reset (searchValue === '' === query.search => no reschedule).
          setSearchValue('');
          onReset();
        }}
        className='col-span-2 h-8 justify-self-start md:col-auto'
      >
        <RotateCcw className='h-4 w-4' />
        Reset
      </Button>

      {uploadSlot && (
        <div
          data-testid='remittance-toolbar-actions'
          className='col-span-2 flex w-full items-center justify-start gap-2 md:col-auto min-[1680px]:ml-auto min-[1680px]:w-auto'
        >
          {uploadSlot}
        </div>
      )}
    </div>
  );
}
